import { AssistChip, Box, ChipGroup } from '@zillow/constellation-10';
import { IconMessageFilled, IconProfileFilled } from '@zillow/constellation-icons';
import { useContext, useEffect, useMemo } from 'react';
import { useMessageListActions_GetConversationDataQuery } from '../../types/GraphqlTypes';
import { ModalType } from '../../types/ModalType';
import {
  emitDisplayRequestRenterProfileButtonEvent,
  emitRequestRenterProfileClickEvent,
  REQUEST_RENTER_PROFILE_CLICK_EVENT_TYPE_ID,
} from '../../constants/clickstreamEvents/requestRenterProfile';
import {
  CHOOSE_SAVED_REPLY_CLICK_EVENT_TYPE_ID,
  emitChooseSavedReplyClickEvent,
} from '../../constants/clickstreamEvents/savedReplyModal';
import StringConstants from '../../constants/StringConstants';
import useModal from '../../hooks/useModal';
import { AppContext } from '../../contexts/AppContextProvider';
import { InboxPageContext } from '../../contexts/InboxPageContextProvider';
import RequestRenterProfileModal from '../Modals/RequestRenterProfileModal';
import SavedReplyModal from '../Modals/SavedReplyModal';
import { TooltipWrapper } from '@zg-rentals/satellite-components';
import useConversationActions from '../../hooks/useConversationActions';
import { useAuth } from '@zg-rentals/auth-comet';

export default function MessageListActions(): JSX.Element {
  const { isImpersonator } = useContext(AppContext);
  const { clientTimeZone, conversationId, listingAlias } = useContext(InboxPageContext);
  const { activeModal, setActiveModal } = useModal();
  const { user } = useAuth();
  const { hasErrorOnRequestRenterProfile, requestRenterProfile } = useConversationActions();

  const { data: conversationData } = useMessageListActions_GetConversationDataQuery({
    variables: {
      clientTimeZone,
      conversationId: conversationId!,
      listingAlias: listingAlias!,
    },
    fetchPolicy: 'cache-only',
  });

  const { inquiry, isConversationBlockedByZillow, referenceName, renterProfile, status } =
    conversationData?.ZRM_getConversationById || {};
  const { shouldShowRequestRenterProfileCta } = renterProfile || {};
  const { isPhoneLead, name } = inquiry || {};
  const { isSpam } = status || {};
  const isChooseSavedReplyDisabled = Boolean(isImpersonator || isPhoneLead || isSpam || isConversationBlockedByZillow);

  useEffect(() => {
    if (shouldShowRequestRenterProfileCta) {
      emitDisplayRequestRenterProfileButtonEvent(user);
    }
  }, [shouldShowRequestRenterProfileCta, user]);

  const chooseSavedReplyTooltipText = useMemo(() => {
    if (isChooseSavedReplyDisabled) {
      if (isPhoneLead) {
        return StringConstants.TOOLTIP.PHONE_LEAD;
      }
      if (isImpersonator) {
        return StringConstants.TOOLTIP.IMPERSONATION;
      }
      if (isSpam) {
        return StringConstants.TOOLTIP.REPORTED;
      }
      if (isConversationBlockedByZillow) {
        return StringConstants.TOOLTIP.BLOCKED_BY_ZILLOW;
      }
    }

    return '';
  }, [isChooseSavedReplyDisabled, isImpersonator, isPhoneLead, isSpam, isConversationBlockedByZillow]);

  const handleShowSavedReplyModal = (): void => {
    setActiveModal(ModalType.SavedReplyModal);
    emitChooseSavedReplyClickEvent(user);
  };

  const handleShowRequestRenterProfileModal = (): void => {
    setActiveModal(ModalType.RequestRenterProfileModal);
    emitRequestRenterProfileClickEvent(user);
  };

  return (
    <>
      {activeModal === ModalType.SavedReplyModal && <SavedReplyModal />}

      {activeModal === ModalType.RequestRenterProfileModal && (
        <RequestRenterProfileModal
          name={name || referenceName || ''}
          onRequestRenterProfileClick={requestRenterProfile}
          hasError={hasErrorOnRequestRenterProfile}
        />
      )}

      <Box
        css={{
          display: 'flex',
          flex: '0 0 auto',
          position: 'sticky',
          bottom: 0,
          padding: 'default',
          width: '98%', // Don't cover scrollbar
          overflowX: 'auto',
          background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.00) 0%, rgba(255, 255, 255, 0.80) 60%, #FFF 100%)',
          marginTop: '-56px',
          zIndex: 10,
        }}
      >
        <ChipGroup aria-label="Message actions" css={{ flexWrap: 'nowrap' }}>
          <TooltipWrapper
            tooltipPlacement="top"
            wrapInTooltip={isChooseSavedReplyDisabled}
            tooltipText={chooseSavedReplyTooltipText}
          >
            <AssistChip
              data-eventid={CHOOSE_SAVED_REPLY_CLICK_EVENT_TYPE_ID}
              aria-haspopup="true"
              aria-label="View saved reply messages"
              disabled={isChooseSavedReplyDisabled}
              onClick={handleShowSavedReplyModal}
              icon={<IconMessageFilled />}
            >
              Choose saved reply
            </AssistChip>
          </TooltipWrapper>

          {shouldShowRequestRenterProfileCta && (
            <TooltipWrapper
              tooltipPlacement="top"
              wrapInTooltip={isImpersonator}
              tooltipText={StringConstants.TOOLTIP.IMPERSONATION}
            >
              <AssistChip
                data-eventid={REQUEST_RENTER_PROFILE_CLICK_EVENT_TYPE_ID}
                aria-haspopup="true"
                aria-label="Request more information"
                disabled={isImpersonator}
                onClick={handleShowRequestRenterProfileModal}
                icon={<IconProfileFilled />}
              >
                Request more information
              </AssistChip>
            </TooltipWrapper>
          )}
        </ChipGroup>
      </Box>
    </>
  );
}
